import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileCheck2,
  Gauge,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  ThumbsDown,
  UsersRound,
  XCircle
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { CleanApprovalApiError, cleanApprovalService } from '@/services/cleanApprovalService';
import type {
  CleanApprovalAdminCandidate,
  CleanApprovalAdminState,
  CleanApprovalCandidateState
} from '@/types/clean-approval';

type RuleAction = 'approved' | 'rejected' | 'disabled';

interface PendingRuleAction {
  candidate: CleanApprovalAdminCandidate;
  status: RuleAction;
}

interface MetricCardProps {
  label: string;
  value: string | number;
  detail: string;
  icon: typeof Gauge;
}

function MetricCard({ label, value, detail, icon: Icon }: MetricCardProps) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold">{value}</p>
          <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
        </div>
        <span className="rounded-md bg-primary/10 p-2 text-primary">
          <Icon className="h-4 w-4" />
        </span>
      </CardContent>
    </Card>
  );
}

function stateLabel(state: CleanApprovalCandidateState): string {
  switch (state) {
    case 'ready_for_review':
      return 'Ready for review';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'disabled':
      return 'Disabled';
    default:
      return 'Learning';
  }
}

function CandidateStateBadge({ state }: { state: CleanApprovalCandidateState }) {
  if (state === 'approved') {
    return (
      <Badge className="gap-1 bg-success text-success-foreground">
        <CheckCircle2 className="h-3 w-3" />
        Approved
      </Badge>
    );
  }
  if (state === 'ready_for_review') {
    return (
      <Badge className="gap-1 bg-warning text-warning-foreground">
        <Sparkles className="h-3 w-3" />
        Ready for review
      </Badge>
    );
  }
  if (state === 'rejected') {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" />
        Rejected
      </Badge>
    );
  }
  if (state === 'disabled') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Ban className="h-3 w-3" />
        Disabled
      </Badge>
    );
  }
  return <Badge variant="outline">Learning</Badge>;
}

function percent(value: number): string {
  if (!Number.isFinite(value)) {
    return '0%';
  }
  return `${Math.round(value * 100)}%`;
}

function formatDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

function actionDescription(action: PendingRuleAction): string {
  if (action.status === 'approved') {
    return `Approve condition ID ${action.candidate.externalId} for automatic preselection? Future Clean Approval drafts may preselect matching open conditions, but staff can still remove them and manual sends still require preview and confirmation.`;
  }
  if (action.status === 'rejected') {
    return `Reject condition ID ${action.candidate.externalId}? It will not be automatically preselected. New manual selections will continue contributing evidence for administrator review.`;
  }
  return `Disable condition ID ${action.candidate.externalId}? It will stop being automatically preselected in new drafts. Existing saved and sent PDFs are not changed.`;
}

export function CleanApprovalConditionsAdmin() {
  const { toast } = useToast();
  const [state, setState] = useState<CleanApprovalAdminState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | CleanApprovalCandidateState>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingRuleAction | null>(null);
  const [reason, setReason] = useState('');
  const [savingRule, setSavingRule] = useState(false);
  const [pendingAutomaticValue, setPendingAutomaticValue] = useState<boolean | null>(null);
  const [savingAutomatic, setSavingAutomatic] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setState(await cleanApprovalService.getAdminState());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Clean Approval automation data could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredCandidates = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (state?.candidates || []).filter((candidate) => {
      if (statusFilter !== 'all' && candidate.state !== statusFilter) {
        return false;
      }
      return !query || [
        candidate.externalId,
        candidate.representativeTitle,
        ...candidate.descriptions
      ].some((value) => value.toLowerCase().includes(query));
    });
  }, [search, state?.candidates, statusFilter]);

  const confirmRuleAction = async () => {
    if (!pendingAction || savingRule) {
      return;
    }
    setSavingRule(true);
    setError(null);
    try {
      await cleanApprovalService.setRuleStatus({
        externalId: pendingAction.candidate.externalId,
        representativeTitle: pendingAction.candidate.representativeTitle,
        status: pendingAction.status,
        reason: reason.trim() || undefined
      });
      toast({
        title: `Condition ${stateLabel(pendingAction.status)}`,
        description: `Condition ID ${pendingAction.candidate.externalId} was updated.`
      });
      setPendingAction(null);
      setReason('');
      await load();
    } catch (saveError) {
      const message = saveError instanceof CleanApprovalApiError && saveError.code === 'RULE_THRESHOLD_NOT_MET'
        ? 'This condition has not yet been selected on 10 distinct reviewed loans.'
        : saveError instanceof Error ? saveError.message : 'The automation rule could not be updated.';
      setError(message);
      setPendingAction(null);
    } finally {
      setSavingRule(false);
    }
  };

  const confirmAutomaticChange = async () => {
    if (pendingAutomaticValue === null || savingAutomatic) {
      return;
    }
    const enabled = pendingAutomaticValue;
    setSavingAutomatic(true);
    setError(null);
    try {
      await cleanApprovalService.setFullyAutomatic(enabled);
      setState((current) => current ? {
        ...current,
        settings: {
          ...current.settings,
          fullyAutomaticEnabled: enabled,
          enabledAt: enabled ? new Date().toISOString() : null,
          updatedAt: new Date().toISOString()
        }
      } : current);
      toast({
        title: enabled ? 'Fully Automatic enabled' : 'Fully Automatic disabled',
        description: enabled
          ? 'Eligible future conditional approvals can now send a Clean Approval to the assigned loan team.'
          : 'New automatic Clean Approval deliveries are disabled. Manual creation remains available.'
      });
      setPendingAutomaticValue(null);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'The organization setting could not be updated.');
      setPendingAutomaticValue(null);
    } finally {
      setSavingAutomatic(false);
    }
  };

  if (loading && !state) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!state) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Clean Approval automation unavailable</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{error || 'The automation state could not be loaded.'}</p>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card className={state.settings.fullyAutomaticEnabled ? 'border-success/40' : undefined}>
        <CardHeader className="pb-3">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Fully Automatic
              </CardTitle>
              <CardDescription className="mt-1 max-w-3xl">
                When enabled, a future conditional-approval completion can generate and email a Clean Approval to the active assigned loan officer and processor. Only administrator-approved condition IDs are preselected.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3 rounded-lg border px-3 py-2">
              <div className="text-right">
                <p className="text-sm font-medium">{state.settings.fullyAutomaticEnabled ? 'Enabled' : 'Disabled'}</p>
                <p className="text-xs text-muted-foreground">Organization-wide</p>
              </div>
              {savingAutomatic ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : (
                <Switch
                  aria-label="Fully Automatic Clean Approval"
                  checked={state.settings.fullyAutomaticEnabled}
                  onCheckedChange={(checked) => setPendingAutomaticValue(checked)}
                />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This setting does not backfill old loans, bypass the historical-loan email guard, or send directly to outside parties. Disable it to stop new automatic deliveries without affecting manual Clean Approvals.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          label="Reviewed loans"
          value={state.metrics.reviewedLoans}
          detail="Saved Clean Approval decisions"
          icon={UsersRound}
        />
        <MetricCard
          label="Ready for review"
          value={state.metrics.readyForReview}
          detail={`${state.metrics.learningIds} IDs still learning`}
          icon={Sparkles}
        />
        <MetricCard
          label="Approved IDs"
          value={state.metrics.approvedRules}
          detail="Eligible for preselection"
          icon={FileCheck2}
        />
        <MetricCard
          label="Auto coverage"
          value={percent(state.metrics.automaticCoverage)}
          detail={`Recent sample: ${state.metrics.metricLoanCount} loans`}
          icon={Gauge}
        />
        <MetricCard
          label="Staff retention"
          value={percent(state.metrics.automationRetention)}
          detail="Preselections staff kept"
          icon={CheckCircle2}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-end">
            <div>
              <CardTitle>Clean Approval condition IDs</CardTitle>
              <CardDescription>
                Evidence reaches review at 10 distinct loans. Reaching the threshold never approves a condition automatically.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh evidence
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search condition ID, title, or external description"
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                <SelectItem value="ready_for_review">Ready for review</SelectItem>
                <SelectItem value="learning">Learning</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"><span className="sr-only">Details</span></TableHead>
                  <TableHead className="w-28">Condition ID</TableHead>
                  <TableHead>Representative title</TableHead>
                  <TableHead className="w-44">Evidence</TableHead>
                  <TableHead className="w-40">State</TableHead>
                  <TableHead className="w-28">Last seen</TableHead>
                  <TableHead className="min-w-56 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                      {state.candidates.length === 0
                        ? 'No Clean Approval decisions have been saved yet.'
                        : 'No condition IDs match these filters.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCandidates.map((candidate) => {
                    const expanded = expandedId === candidate.externalId;
                    const progress = Math.min(candidate.distinctLoans, 10);
                    return (
                      <Fragment key={candidate.externalId}>
                        <TableRow>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => setExpandedId(expanded ? null : candidate.externalId)}
                              aria-label={`${expanded ? 'Hide' : 'Show'} evidence for ${candidate.externalId}`}
                            >
                              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{candidate.externalId}</TableCell>
                          <TableCell className="font-medium">{candidate.representativeTitle}</TableCell>
                          <TableCell>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between text-xs">
                                <span>{candidate.distinctLoans} distinct loan{candidate.distinctLoans === 1 ? '' : 's'}</span>
                                {candidate.distinctLoans < 10 && <span className="text-muted-foreground">{10 - candidate.distinctLoans} to review</span>}
                              </div>
                              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                                <div
                                  className={`h-full rounded-full ${candidate.distinctLoans >= 10 ? 'bg-success' : 'bg-primary'}`}
                                  style={{ width: `${progress * 10}%` }}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell><CandidateStateBadge state={candidate.state} /></TableCell>
                          <TableCell className="text-sm text-muted-foreground">{formatDate(candidate.lastSelectedAt)}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1.5">
                              {candidate.state !== 'approved' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={!candidate.canApprove}
                                  onClick={() => {
                                    setReason('');
                                    setPendingAction({ candidate, status: 'approved' });
                                  }}
                                >
                                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                  {candidate.state === 'disabled' ? 'Re-enable' : 'Approve'}
                                </Button>
                              )}
                              {(candidate.state === 'ready_for_review' || candidate.state === 'rejected') && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setReason('');
                                    setPendingAction({ candidate, status: 'rejected' });
                                  }}
                                >
                                  <ThumbsDown className="mr-1.5 h-3.5 w-3.5" />
                                  Reject
                                </Button>
                              )}
                              {candidate.state === 'approved' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setReason('');
                                    setPendingAction({ candidate, status: 'disabled' });
                                  }}
                                >
                                  <Ban className="mr-1.5 h-3.5 w-3.5" />
                                  Disable
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {expanded && (
                          <TableRow className="bg-muted/20">
                            <TableCell />
                            <TableCell colSpan={6}>
                              <div className="grid gap-5 py-2 lg:grid-cols-2">
                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Observed external descriptions</p>
                                  {candidate.descriptions.length > 0 ? (
                                    <ul className="mt-2 space-y-2">
                                      {candidate.descriptions.slice(0, 5).map((description) => (
                                        <li key={description} className="rounded-md border bg-background p-3 text-sm whitespace-pre-wrap">
                                          {description}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="mt-2 text-sm italic text-muted-foreground">No non-empty external descriptions were observed.</p>
                                  )}
                                </div>
                                <dl className="grid grid-cols-[130px_1fr] content-start gap-x-3 gap-y-2 text-sm">
                                  <dt className="text-muted-foreground">First selected</dt>
                                  <dd>{formatDate(candidate.firstSelectedAt)}</dd>
                                  <dt className="text-muted-foreground">Last selected</dt>
                                  <dd>{formatDate(candidate.lastSelectedAt)}</dd>
                                  <dt className="text-muted-foreground">Reviewed</dt>
                                  <dd>{formatDate(candidate.reviewedAt)}</dd>
                                  <dt className="text-muted-foreground">Review note</dt>
                                  <dd>{candidate.reviewReason || '—'}</dd>
                                </dl>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={pendingAction !== null} onOpenChange={(open) => {
        if (!open && !savingRule) {
          setPendingAction(null);
          setReason('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.status === 'approved'
                ? 'Approve automatic preselection rule?'
                : pendingAction?.status === 'rejected'
                  ? 'Reject this candidate?'
                  : 'Disable automatic preselection rule?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction ? actionDescription(pendingAction) : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor="clean-approval-review-reason" className="text-sm font-medium">
              Review note <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              id="clean-approval-review-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Record why this condition ID is or is not appropriate for outside-party approvals"
              maxLength={1000}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingRule}>Cancel</AlertDialogCancel>
            <Button
              variant={pendingAction?.status === 'approved' ? 'default' : 'destructive'}
              onClick={() => void confirmRuleAction()}
              disabled={savingRule}
            >
              {savingRule && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm {pendingAction ? stateLabel(pendingAction.status).toLowerCase() : ''}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingAutomaticValue !== null} onOpenChange={(open) => {
        if (!open && !savingAutomatic) {
          setPendingAutomaticValue(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAutomaticValue ? 'Enable Fully Automatic Clean Approvals?' : 'Disable Fully Automatic Clean Approvals?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAutomaticValue
                ? 'For future eligible conditional approvals, the system may generate a Clean Approval using only administrator-approved condition IDs and email it to the active assigned loan officer and processor. It will not email outside parties, and no historical loans are backfilled.'
                : 'New automatic deliveries will stop immediately. Approved condition-ID rules and manual Clean Approval workflows will remain available.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingAutomaticValue && (
            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="mb-3 text-sm font-medium">Current readiness snapshot</p>
              <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Approved condition IDs</dt>
                <dd className="font-medium">{state.metrics.approvedRules}</dd>
                <dt className="text-muted-foreground">Automatic coverage</dt>
                <dd className="font-medium">{percent(state.metrics.automaticCoverage)}</dd>
                <dt className="text-muted-foreground">Staff retention</dt>
                <dd className="font-medium">{percent(state.metrics.automationRetention)}</dd>
              </dl>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingAutomatic}>Cancel</AlertDialogCancel>
            <Button
              variant={pendingAutomaticValue ? 'default' : 'destructive'}
              onClick={() => void confirmAutomaticChange()}
              disabled={savingAutomatic}
            >
              {savingAutomatic && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {pendingAutomaticValue ? 'Enable Fully Automatic' : 'Disable Fully Automatic'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
